/**
 * Data Diagnostics Page
 * 
 * Helps identify data inconsistencies between:
 * - Seed/demo data
 * - User-created data
 * - Local storage vs Supabase
 */

import { useState, useEffect } from 'react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { 
  Database, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  RefreshCw,
  Download,
  Copy,
  Table,
} from 'lucide-react';
import { getSupabaseClient } from '../../../lib/supabaseClient';
import { isSupabaseAvailable, getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import {
  ALL_SCHEMAS,
  generateAddColumnSQL,
  type ColumnDefinition,
} from '../../../lib/schemaDefinitions';

interface DiagnosticResult {
  field: string;
  localCount: number;
  supabaseCount: number;
  status: 'ok' | 'warning' | 'error';
  message: string;
}

interface SchemaCheckResult {
  tableName: string;
  expectedColumns: ColumnDefinition[];
  actualColumns: string[];
  missingColumns: ColumnDefinition[];
  sql: string;
}

export default function DataDiagnostics() {
  const [loading, setLoading] = useState(false);
  const [supabaseSubmissions, setSupabaseSubmissions] = useState<any[]>([]);
  const [supabaseCandidates, setSupabaseCandidates] = useState<any[]>([]);
  const [diagnostics, setDiagnostics] = useState<DiagnosticResult[]>([]);
  const [schemaChecks, setSchemaChecks] = useState<SchemaCheckResult[]>([]);
  const [copiedSql, setCopiedSql] = useState<string | null>(null);
  
  const localItems = useItemsStore((state) => state.items);
  const orgUnits = useEnterpriseIdentityStore((state) => state.orgUnits);
  
  const runDiagnostics = async () => {
    setLoading(true);
    const results: DiagnosticResult[] = [];
    
    try {
      if (!isSupabaseAvailable()) {
        results.push({
          field: 'Supabase Connection',
          localCount: 0,
          supabaseCount: 0,
          status: 'error',
          message: 'Supabase is not configured or not available',
        });
        setDiagnostics(results);
        setLoading(false);
        return;
      }
      
      const supabase = getSupabaseClient();
      const workspaceId = getCurrentWorkspaceId();
      
      // Fetch idea_submissions from Supabase
      let submissionsQuery = supabase.from('idea_submissions').select('*');
      if (workspaceId) {
        submissionsQuery = submissionsQuery.eq('workspace_id', workspaceId);
      }
      const { data: submissions, error: subError } = await submissionsQuery;
      
      if (subError) {
        results.push({
          field: 'idea_submissions',
          localCount: 0,
          supabaseCount: 0,
          status: 'error',
          message: `Query failed: ${subError.message}`,
        });
      } else {
        setSupabaseSubmissions(submissions || []);
        results.push({
          field: 'idea_submissions',
          localCount: 0,
          supabaseCount: submissions?.length || 0,
          status: 'ok',
          message: `${submissions?.length || 0} records in Supabase`,
        });
        
        // Check for common data issues in submissions
        const submissionsWithoutStatus = submissions?.filter(s => !s.status) || [];
        const submissionsWithCandidateId = submissions?.filter(s => s.promoted_to_item_id) || [];
        
        if (submissionsWithoutStatus.length > 0) {
          results.push({
            field: 'idea_submissions.status',
            localCount: 0,
            supabaseCount: submissionsWithoutStatus.length,
            status: 'warning',
            message: `${submissionsWithoutStatus.length} submissions missing status field`,
          });
        }
        
        results.push({
          field: 'idea_submissions.promoted_to_item_id',
          localCount: 0,
          supabaseCount: submissionsWithCandidateId.length,
          status: submissionsWithCandidateId.length > 0 ? 'ok' : 'warning',
          message: `${submissionsWithCandidateId.length} of ${submissions?.length || 0} have linked candidates`,
        });
      }
      
      // Fetch candidates from Supabase
      let candidatesQuery = supabase.from('items').select('*');
      if (workspaceId) {
        candidatesQuery = candidatesQuery.eq('workspace_id', workspaceId);
      }
      const { data: candidates, error: candError } = await candidatesQuery;
      
      if (candError) {
        results.push({
          field: 'candidates',
          localCount: localItems.length,
          supabaseCount: 0,
          status: 'error',
          message: `Query failed: ${candError.message}`,
        });
      } else {
        setSupabaseCandidates(candidates || []);
        
        const localOnly = localItems.filter(lc => 
          !candidates?.some(sc => sc.id === lc.id)
        );
        const supabaseOnly = candidates?.filter(sc =>
          !localItems.some(lc => lc.id === sc.id)
        ) || [];
        
        results.push({
          field: 'candidates (total)',
          localCount: localItems.length,
          supabaseCount: candidates?.length || 0,
          status: localItems.length === (candidates?.length || 0) ? 'ok' : 'warning',
          message: localItems.length === (candidates?.length || 0) 
            ? 'Counts match'
            : `Mismatch: ${localOnly.length} local-only, ${supabaseOnly.length} supabase-only`,
        });
        
        if (localOnly.length > 0) {
          results.push({
            field: 'candidates (local-only)',
            localCount: localOnly.length,
            supabaseCount: 0,
            status: 'warning',
            message: `${localOnly.length} candidates exist only in local storage: ${localOnly.map(c => c.title).join(', ')}`,
          });
        }
        
        // Check for lifecycle_state distribution
        const stateDistribution = new Map<string, number>();
        localItems.forEach(c => {
          const state = c.lifecycle_state || 'undefined';
          stateDistribution.set(state, (stateDistribution.get(state) || 0) + 1);
        });
        
        results.push({
          field: 'candidates.lifecycle_state distribution',
          localCount: localItems.length,
          supabaseCount: candidates?.length || 0,
          status: 'ok',
          message: Array.from(stateDistribution.entries()).map(([k, v]) => `${k}: ${v}`).join(', '),
        });
      }
      
      // Check org units
      const localOrgUnitsCount = orgUnits.size;
      const orgUnitsWithQueues = Array.from(orgUnits.values()).filter(ou => ou.queueSettings?.enabled).length;
      
      results.push({
        field: 'org_units (local)',
        localCount: localOrgUnitsCount,
        supabaseCount: 0,
        status: localOrgUnitsCount > 0 ? 'ok' : 'warning',
        message: `${localOrgUnitsCount} org units, ${orgUnitsWithQueues} with queues enabled`,
      });
      
      // ======================================================================
      // SCHEMA CHECKS - Compare expected columns vs actual Supabase columns
      // ======================================================================
      const schemaResults: SchemaCheckResult[] = [];
      
      for (const schema of ALL_SCHEMAS) {
        try {
          // Use information_schema RPC so empty tables don't produce false positives
          const { data: columnRows, error: rpcError } = await supabase
            .rpc('get_table_columns', { p_table_name: schema.tableName });

          let actualColumns: string[];

          if (rpcError) {
            // RPC may not exist yet — fall back to row-based detection with a note
            const { data: sampleData, error: sampleError } = await supabase
              .from(schema.tableName)
              .select('*')
              .limit(1);

            if (sampleError) {
              schemaResults.push({
                tableName: schema.tableName,
                expectedColumns: schema.columns,
                actualColumns: [],
                missingColumns: schema.columns,
                sql: `-- Could not query ${schema.tableName}: ${sampleError.message}`,
              });
              continue;
            }

            actualColumns = sampleData && sampleData.length > 0
              ? Object.keys(sampleData[0])
              : [];

            if (actualColumns.length === 0) {
              // Table exists but is empty and RPC unavailable — skip schema check
              schemaResults.push({
                tableName: schema.tableName,
                expectedColumns: schema.columns,
                actualColumns: [],
                missingColumns: [],
                sql: `-- Table ${schema.tableName} is empty; run migration 308 to enable accurate schema checks`,
              });
              results.push({
                field: `${schema.tableName} schema`,
                localCount: schema.columns.length,
                supabaseCount: 0,
                status: 'warning',
                message: 'Table is empty — schema could not be verified. Run migration 308 to enable accurate checks.',
              });
              continue;
            }
          } else {
            actualColumns = (columnRows as Array<{ column_name: string }> | null)
              ?.map((r) => r.column_name) ?? [];
          }
          
          // Find missing columns
          const missingColumns = schema.columns.filter(col => 
            !actualColumns.includes(col.name)
          );
          
          const sql = generateAddColumnSQL(schema.tableName, missingColumns);
          
          schemaResults.push({
            tableName: schema.tableName,
            expectedColumns: schema.columns,
            actualColumns,
            missingColumns,
            sql,
          });
          
          // Add diagnostic result for missing columns
          if (missingColumns.length > 0) {
            results.push({
              field: `${schema.tableName} schema`,
              localCount: schema.columns.length,
              supabaseCount: actualColumns.length,
              status: 'error',
              message: `Missing ${missingColumns.length} columns: ${missingColumns.map(c => c.name).join(', ')}`,
            });
          } else {
            results.push({
              field: `${schema.tableName} schema`,
              localCount: schema.columns.length,
              supabaseCount: actualColumns.length,
              status: 'ok',
              message: 'All expected columns present',
            });
          }
        } catch (schemaErr) {
          console.error(`Schema check failed for ${schema.tableName}:`, schemaErr);
        }
      }
      
      setSchemaChecks(schemaResults);
      
    } catch (err) {
      results.push({
        field: 'General',
        localCount: 0,
        supabaseCount: 0,
        status: 'error',
        message: `Diagnostic failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
      });
    }
    
    setDiagnostics(results);
    setLoading(false);
  };
  
  useEffect(() => {
    runDiagnostics();
  }, []);
  
  const getStatusIcon = (status: DiagnosticResult['status']) => {
    switch (status) {
      case 'ok':
        return <CheckCircle className="h-5 w-5 text-[var(--green)]" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-[var(--mustard)]" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-[var(--coral)]" />;
    }
  };
  
  const exportData = () => {
    const exportObj = {
      timestamp: new Date().toISOString(),
      diagnostics,
      supabaseSubmissions,
      supabaseCandidates,
      localItems: localItems.map(c => ({
        id: c.id,
        title: c.title,
        lifecycle_state: c.lifecycle_state,
        detailedState: c.detailedState,
        queueId: c.queueId,
        createdAt: c.createdAt,
      })),
      orgUnits: Array.from(orgUnits.entries()),
    };
    
    const blob = new Blob([JSON.stringify(exportObj, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `data-diagnostics-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Database className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Data Diagnostics"
        subtitle="Compare local storage and Supabase data to find inconsistencies"
        actions={
          <>
            <button
              onClick={runDiagnostics}
              disabled={loading}
              className="btn btn-secondary flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={exportData}
              className="btn btn-primary flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export All Data
            </button>
          </>
        }
      />
      
      {/* Diagnostics Results */}
      <div className="card">
        <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Diagnostic Results</h2>
        
        {loading ? (
          <div className="text-center py-8 text-[var(--text-ter)]">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
            Running diagnostics...
          </div>
        ) : (
          <div className="space-y-2">
            {diagnostics.map((result, i) => (
              <div
                key={i}
                className={`p-3  border ${
                  result.status === 'ok' ? 'bg-[var(--green-dim)] border-[var(--green)]' :
                  result.status === 'warning' ? 'bg-[var(--mustard-dim)] border-[var(--mustard)]' :
                  'bg-[var(--coral-dim)] border-[var(--coral)]'
                }`}
              >
                <div className="flex items-start gap-3">
                  {getStatusIcon(result.status)}
                  <div className="flex-1">
                    <div className="font-medium text-[var(--text-pri)]">{result.field}</div>
                    <div className="text-sm text-[var(--text-sec)]">{result.message}</div>
                    {(result.localCount > 0 || result.supabaseCount > 0) && (
                      <div className="text-xs text-[var(--text-ter)] mt-1">
                        Local: {result.localCount} | Supabase: {result.supabaseCount}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Schema Check - SQL to Run */}
      {schemaChecks.some(sc => sc.missingColumns.length > 0) && (
        <div className="card bg-[var(--coral-dim)] border-[var(--coral)]">
          <div className="flex items-center gap-2 mb-4">
            <Table className="h-6 w-6 text-[var(--coral)]" />
            <h2 className="text-lg font-semibold text-[var(--coral)]">
              Schema Update Required
            </h2>
          </div>
          <p className="text-sm text-[var(--coral)] mb-4">
            The following columns are missing from your Supabase tables. 
            Run this SQL in your Supabase SQL Editor to add them:
          </p>
          
          {schemaChecks.filter(sc => sc.missingColumns.length > 0).map((sc) => (
            <div key={sc.tableName} className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium text-[var(--coral)]">{sc.tableName}</h3>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(sc.sql);
                    setCopiedSql(sc.tableName);
                    setTimeout(() => setCopiedSql(null), 2000);
                  }}
                  className="flex items-center gap-1 px-2 py-1 text-xs bg-[var(--coral-dim)] hover:bg-[var(--coral-dim)] text-[var(--coral)] rounded"
                >
                  <Copy className="h-3 w-3" />
                  {copiedSql === sc.tableName ? 'Copied!' : 'Copy SQL'}
                </button>
              </div>
              <div className="text-xs text-[var(--coral)] mb-2">
                Missing: {sc.missingColumns.map(c => c.name).join(', ')}
              </div>
              <pre className="bg-[var(--bg)] text-[var(--green)] dark:text-[var(--text-ter)] p-3 rounded text-xs overflow-x-auto">
                {sc.sql}
              </pre>
            </div>
          ))}
          
          <div className="mt-4 p-3 bg-[var(--coral-dim)]">
            <p className="text-sm text-[var(--coral)] font-medium">
              After running the SQL, click "Refresh" to verify the columns were added.
            </p>
          </div>
        </div>
      )}
      
      {/* Schema OK */}
      {schemaChecks.length > 0 && schemaChecks.every(sc => sc.missingColumns.length === 0) && (
        <div className="card bg-[var(--green-dim)] border-[var(--green)]">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-6 w-6 text-[var(--green)]" />
            <div>
              <h2 className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">Schema Up to Date</h2>
              <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                All expected columns exist in your Supabase tables. No SQL needed.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Raw Data Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Supabase Submissions */}
        <div className="card">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">
            idea_submissions ({supabaseSubmissions.length})
          </h3>
          <div className="overflow-auto max-h-96">
            <table className="w-full text-xs">
              <thead className="bg-[var(--surface)] sticky top-0">
                <tr>
                  <th className="px-2 py-1 text-left">ID</th>
                  <th className="px-2 py-1 text-left">Title</th>
                  <th className="px-2 py-1 text-left">Status</th>
                  <th className="px-2 py-1 text-left">Candidate ID</th>
                </tr>
              </thead>
              <tbody>
                {supabaseSubmissions.map((sub) => (
                  <tr key={sub.id} className="border-t hover:bg-[var(--surface)]">
                    <td className="px-2 py-1 font-mono text-[10px]">{sub.id?.slice(0, 8)}...</td>
                    <td className="px-2 py-1 truncate max-w-[150px]">{sub.title}</td>
                    <td className="px-2 py-1">
                      <span className={`px-1 py-0.5 rounded text-[10px] ${
                        sub.status === 'INTAKE_PENDING' ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' :
                        sub.status === 'SHAPING' ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' :
                        'bg-[var(--hover-tint)] text-[var(--text-pri)]'
                      }`}>
                        {sub.status || 'null'}
                      </span>
                    </td>
                    <td className="px-2 py-1 font-mono text-[10px]">
                      {sub.promoted_to_item_id ? (
                        <span className="text-[var(--green)] dark:text-[var(--text-ter)]">{sub.promoted_to_item_id.slice(0, 8)}...</span>
                      ) : (
                        <span className="text-[var(--coral)]">null</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Local Candidates */}
        <div className="card">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">
            Local Candidates ({localItems.length})
          </h3>
          <div className="overflow-auto max-h-96">
            <table className="w-full text-xs">
              <thead className="bg-[var(--surface)] sticky top-0">
                <tr>
                  <th className="px-2 py-1 text-left">ID</th>
                  <th className="px-2 py-1 text-left">Title</th>
                  <th className="px-2 py-1 text-left">State</th>
                  <th className="px-2 py-1 text-left">Detailed</th>
                </tr>
              </thead>
              <tbody>
                {localItems.map((cand) => (
                  <tr key={cand.id} className="border-t hover:bg-[var(--surface)]">
                    <td className="px-2 py-1 font-mono text-[10px]">{cand.id?.slice(0, 8)}...</td>
                    <td className="px-2 py-1 truncate max-w-[150px]">{cand.title}</td>
                    <td className="px-2 py-1">
                      <span className={`px-1 py-0.5 rounded text-[10px] ${
                        cand.lifecycle_state === 'item' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' :
                        'bg-[var(--hover-tint)] text-[var(--text-pri)]'
                      }`}>
                        {cand.lifecycle_state === 'item' ? 'item' : (cand.lifecycle_state || 'null')}
                      </span>
                    </td>
                    <td className="px-2 py-1">
                      <span className="px-1 py-0.5 rounded text-[10px] bg-[var(--hover-tint)] text-[var(--text-pri)]">
                        {cand.detailedState || 'null'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      {/* Field Comparison */}
      <div className="card">
        <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">
          Field Presence Check
        </h3>
        <p className="text-sm text-[var(--text-sec)] mb-4">
          Compares which fields are present in seed data vs user-created data
        </p>
        
        {supabaseSubmissions.length > 0 && (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--surface)]">
                <tr>
                  <th className="px-3 py-2 text-left">Field</th>
                  <th className="px-3 py-2 text-center">Present Count</th>
                  <th className="px-3 py-2 text-center">Null Count</th>
                  <th className="px-3 py-2 text-left">Sample Values</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(supabaseSubmissions[0] || {}).map((field) => {
                  const presentCount = supabaseSubmissions.filter(s => s[field] != null).length;
                  const nullCount = supabaseSubmissions.length - presentCount;
                  const sampleValues = supabaseSubmissions
                    .filter(s => s[field] != null)
                    .slice(0, 3)
                    .map(s => String(s[field]).slice(0, 30));
                  
                  return (
                    <tr key={field} className="border-t">
                      <td className="px-3 py-2 font-mono text-xs">{field}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={presentCount === supabaseSubmissions.length ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--mustard)]'}>
                          {presentCount}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={nullCount > 0 ? 'text-[var(--coral)]' : 'text-[var(--text-ter)]'}>
                          {nullCount}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-[var(--text-ter)] truncate max-w-[300px]">
                        {sampleValues.join(' | ')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
